import toast from 'react-hot-toast';
import { FlowStep, StepVariant, Storefront, flowStepToNode, StepType } from '../types';
import { createNewStep, findAvailablePosition } from '../utils/helpers';

interface UseFunnelHandlersProps {
  funnel: Storefront;
  setFunnel: (funnel: Storefront) => void;
  selectedStepId: string | null;
  setSelectedStepId: (id: string | null) => void;
}

/**
 * Custom hook for funnel manipulation handlers
 * Encapsulates all step CRUD operations and transformations
 */
export function useFunnelHandlers({
  funnel,
  setFunnel,
  selectedStepId,
  setSelectedStepId,
}: UseFunnelHandlersProps) {
  // Handle step updates
  const handleStepChange = (updatedStep: FlowStep) => {
    const newSteps = (Array.isArray(funnel.steps) ? funnel.steps : [])
      .filter((s) => s)
      .map((step) => (step.id === updatedStep.id ? updatedStep : step));

    // Sync node
    const { node: updatedNode } = flowStepToNode(updatedStep);
    const newNodes = (funnel.nodes || []).map((n) => (n.id === updatedStep.id ? updatedNode : n));

    setFunnel({
      ...funnel,
      steps: newSteps,
      nodes: newNodes,
    });
  };

  // Copy one step's pixel configuration to every other step (TAG-1213).
  // Pixels are stored per step (config.stepConfig.pixels) but merchants
  // assume they're funnel-wide — this makes the "apply everywhere" intent
  // one click. Writes each target step's main config AND every variant
  // config in a single setFunnel (looping handleStepChange would clobber:
  // it closes over a stale `funnel`). Returns false when the source step
  // has no pixels to copy.
  const handleCopyPixelsToAllSteps = (fromStepId: string): boolean => {
    const steps = (Array.isArray(funnel.steps) ? funnel.steps : []).filter((s) => s);
    const source = steps.find((s) => s.id === fromStepId);
    const pixels =
      source?.config?.stepConfig?.pixels ??
      source?.variants?.[0]?.config?.stepConfig?.pixels;
    if (!pixels || Object.keys(pixels).length === 0) return false;

    const withPixels = <C extends FlowStep['config']>(config: C): C => {
      if (!config) return config;
      return {
        ...config,
        stepConfig: {
          ...(config.stepConfig || {}),
          pixels: JSON.parse(JSON.stringify(pixels)),
        },
      };
    };

    const newSteps = steps.map((step) => {
      if (step.id === fromStepId) return step;
      return {
        ...step,
        config: withPixels(step.config),
        variants: step.variants?.map((v) => ({ ...v, config: withPixels(v.config) })),
      };
    });

    const stepById = new Map(newSteps.map((s) => [s.id, s]));
    const newNodes = (funnel.nodes || []).map((n) => {
      const updated = stepById.get(n.id);
      if (!updated || n.id === fromStepId) return n;
      const { node } = flowStepToNode(updated);
      return node;
    });

    setFunnel({ ...funnel, steps: newSteps, nodes: newNodes });
    return true;
  };

  // Handle adding conditional step (new row with different condition)
  const handleAddConditionalStep = (col: number) => {
    const validSteps = (Array.isArray(funnel.steps) ? funnel.steps : []).filter((s) => s && s.position);
    const stepsInCol = validSteps.filter((s) => s.position.col === col);
    const newRow = stepsInCol.length;
    const alternativeLetter = String.fromCharCode(65 + newRow); // A, B, C, etc.
    const newStep = createNewStep(col, newRow, `Alternative ${alternativeLetter}`);

    // Set as conditional alternative
    newStep.alternativeType = 'conditional';
    newStep.conditions.isDefault = false;
    newStep.conditions.priority = stepsInCol.length;

    // Copy page configuration from the first step in the column
    const firstStep = stepsInCol[0];
    if (firstStep) {
      newStep.type = firstStep.type;
      const sourceConfig = firstStep.variants ? firstStep.variants[0].config : firstStep.config;
      if (sourceConfig && newStep.config) {
        newStep.config.pagePath = sourceConfig.pagePath;
        newStep.config.mountPointId = sourceConfig.mountPointId;
        // Copy metadata but strip routing fields that the backend must recompute.
        // Carrying over routeId/deploymentId/pluginId from the source would cause
        // the new step to collide with the source step's KV route.
        const { deploymentId, pluginId, customPath, pathRemap, routeType, ...safeMetadata } = sourceConfig.metadata || {};
        newStep.config.metadata = { ...safeMetadata };
      }
    }

    const { node } = flowStepToNode(newStep);

    setFunnel({
      ...funnel,
      steps: [...(Array.isArray(funnel.steps) ? funnel.steps : []), newStep],
      nodes: [...(funnel.nodes || []), node],
    });

    setSelectedStepId(null);
    toast.success(`Alternative version added to Page ${col + 1}. Click to edit its condition.`);
  };

  // Handle transforming a standard step into a split step with A/B variants
  const handleTransformToSplit = (stepId: string, variants: StepVariant[], splitType: 'weighted' | 'geo') => {
    const updatedSteps = (Array.isArray(funnel.steps) ? funnel.steps : [])
      .filter((s) => s)
      .map((step) => {
        if (step.id === stepId) {
          return {
            ...step,
            config: undefined,
            variants,
            splitType,
          };
        }
        return step;
      });

    const updatedNodes = (funnel.nodes || []).map((n) => {
      if (n.id === stepId) {
        return {
          ...n,
          config: undefined,
          variants,
          splitType,
        };
      }
      return n;
    });

    setFunnel({
      ...funnel,
      steps: updatedSteps,
      nodes: updatedNodes,
    });

    const typeLabel = splitType === 'weighted' ? 'percentage-based' : 'location-based';
    toast.success(`A/B test created (${typeLabel}) with ${variants.length} versions`);
  };

  // Handle updating variants for an existing split step
  const handleUpdateVariants = (stepId: string, variants: StepVariant[], splitType?: 'weighted' | 'geo') => {
    // Find the original step/node to preserve its properties and match variant configs
    const originalStep = (Array.isArray(funnel.steps) ? funnel.steps : []).find((s) => s?.id === stepId);
    const originalNode = (funnel.nodes || []).find((n) => n.id === stepId);
    const originalVariants = originalStep?.variants || originalNode?.variants || [];
    
    // Create a map of original variant configs by variant ID
    const originalVariantConfigs = new Map<string, StepVariant['config']>();
    originalVariants.forEach((v) => {
      if (v.id && v.config) {
        originalVariantConfigs.set(v.id, v.config);
      }
    });
    
    // Ensure variants preserve their config from original variants
    const variantsWithConfig = variants.map((variant) => {
      // If variant already has config, use it; otherwise try to get from original
      const config = variant.config || originalVariantConfigs.get(variant.id);
      return {
        ...variant,
        config, // Explicitly preserve config
      };
    });
    
    const updatedSteps = (Array.isArray(funnel.steps) ? funnel.steps : [])
      .filter((s) => s)
      .map((step) => {
        if (step.id === stepId) {
          return {
            ...step,
            variants: variantsWithConfig, // Use variants with preserved configs
            splitType: splitType || step.splitType,
            config: step.config, // Preserve step-level config
          };
        }
        return step;
      });

    const updatedNodes = (funnel.nodes || []).map((n) => {
      if (n.id === stepId) {
        return {
          ...n,
          variants: variantsWithConfig, // Use variants with preserved configs
          splitType: splitType || n.splitType,
          config: n.config, // Preserve node-level config
        };
      }
      return n;
    });

    setFunnel({
      ...funnel,
      steps: updatedSteps,
      nodes: updatedNodes,
    });

    const typeLabel = splitType === 'geo' ? 'location-based' : 'percentage-based';
    toast.success(`A/B test updated (${typeLabel})`);
  };

  // Handle removing split test (convert back to standard step)
  const handleRemoveSplit = (stepId: string, variantToKeep: StepVariant) => {
    const updatedSteps = (Array.isArray(funnel.steps) ? funnel.steps : [])
      .filter((s) => s)
      .map((step) => {
        if (step.id === stepId) {
          return {
            ...step,
            config: variantToKeep.config,
            variants: undefined,
            splitType: undefined,
          };
        }
        return step;
      });

    const updatedNodes = (funnel.nodes || []).map((n) => {
      if (n.id === stepId) {
        return {
          ...n,
          config: variantToKeep.config,
          variants: undefined,
          splitType: undefined,
        };
      }
      return n;
    });

    setFunnel({
      ...funnel,
      steps: updatedSteps,
      nodes: updatedNodes,
    });

    toast.success(`A/B test removed. Kept version "${variantToKeep.label}"`);
  };

  // Handle adding a new column (can insert at specific position)
  const handleAddColumn = (insertAfterCol?: number, stepType?: StepType) => {
    let updatedSteps = [...(Array.isArray(funnel.steps) ? funnel.steps : [])];
    let updatedNodes = [...(funnel.nodes || [])];

    if (insertAfterCol !== undefined) {
      // Special case: insertAfterCol = -1 means insert at the beginning
      const insertPosition = insertAfterCol === -1 ? 0 : insertAfterCol + 1;

      // Shift all columns at or after the insert position
      updatedSteps = updatedSteps.map((step) => {
        if (step.position.col >= insertPosition) {
          return {
            ...step,
            position: { ...step.position, col: step.position.col + 1 },
            // Also update flowPosition x if present
            flowPosition: step.flowPosition
              ? { ...step.flowPosition, x: step.flowPosition.x + 400 }
              : undefined,
          };
        }
        return step;
      });

      // Sync nodes (shift them too)
      updatedNodes = updatedNodes.map((node) => {
        const correspondingStep = updatedSteps.find((s) => s.id === node.id);
        if (correspondingStep) {
          return {
            ...node,
            index: correspondingStep.position.col,
            row: correspondingStep.position.row,
            position: correspondingStep.flowPosition || node.position,
          };
        }
        return node;
      });

      const newStep = createNewStep(insertPosition, 0, undefined, stepType, updatedSteps);
      updatedSteps.push(newStep);

      const { node: newNode } = flowStepToNode(newStep);
      updatedNodes.push(newNode);

      if (insertAfterCol === -1) {
        toast.success('New page added at the beginning');
      } else {
        toast.success(`New page inserted after Page ${insertAfterCol + 1}`);
      }
    } else {
      const maxCol = Math.max(...(Array.isArray(funnel.steps) ? funnel.steps : []).map((s) => s.position.col), -1);
      const newCol = maxCol + 1;

      const existingSteps = Array.isArray(funnel.steps) ? funnel.steps : [];
      const newStep = createNewStep(newCol, 0, undefined, stepType, existingSteps);

      // Smart positioning: Use actual canvas nodes (which have position) to find rightmost
      const smartPosition = findAvailablePosition(funnel.nodes || []);
      newStep.flowPosition = smartPosition;

      updatedSteps.push(newStep);

      const { node: newNode } = flowStepToNode(newStep);

      updatedNodes.push(newNode);

      toast.success('New page added');
    }

    setFunnel({
      ...funnel,
      steps: updatedSteps,
      nodes: updatedNodes,
    });

    setSelectedStepId(null);
  };

  // Handle deleting a step
  const handleStepDelete = (stepId: string) => {
    const newSteps = (Array.isArray(funnel.steps) ? funnel.steps : []).filter((step) => step && step.id !== stepId);
    const newNodes = (funnel.nodes || []).filter((n) => n.id !== stepId);
    const newEdges = (funnel.edges || []).filter((e) => e.source !== stepId && e.target !== stepId);

    setFunnel({
      ...funnel,
      steps: newSteps,
      nodes: newNodes,
      edges: newEdges,
    });

    if (selectedStepId === stepId) {
      setSelectedStepId(null);
    }

    toast.success('Page deleted');
  };

  return {
    handleStepChange,
    handleCopyPixelsToAllSteps,
    handleAddConditionalStep,
    handleTransformToSplit,
    handleUpdateVariants,
    handleRemoveSplit,
    handleAddColumn,
    handleStepDelete,
  };
}

